/**
 * Modal Manager for Flowbite Modals
 *
 * Manages modal instances with proper TypeScript support for Flowbite
 * Handles dynamic content and proper cleanup
 */

import { Modal, type ModalOptions, type ModalInterface, type InstanceOptions } from 'flowbite';

export class ModalManager {
  private static instance: ModalManager;
  private modalInstances = new Map<string, ModalInterface>();
  private isEventDelegationSetup = false;

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): ModalManager {
    if (!ModalManager.instance) {
      ModalManager.instance = new ModalManager();
    }
    return ModalManager.instance;
  }

  /**
   * Initialize or get modal instance
   */
  public getModal(
    modalId: string,
    options?: ModalOptions,
    instanceOptions?: InstanceOptions
  ): ModalInterface | null {
    const modalElement = document.getElementById(modalId) as HTMLElement;

    if (!modalElement) {
      console.warn(`[ModalManager] Modal element with ID "${modalId}" not found`);
      return null;
    }

    // If modal instance already exists, return it
    if (this.modalInstances.has(modalId)) {
      const existingModal = this.modalInstances.get(modalId);
      return existingModal || null;
    }

    // Default modal options
    const defaultOptions: ModalOptions = {
      placement: 'center',
      backdrop: 'dynamic',
      backdropClasses: 'bg-gray-900/50 fixed inset-0 z-40',
      closable: true,
      onHide: modal => {
        // Optional cleanup when modal is hidden
        if (options?.onHide) {
          options.onHide(modal);
        }
      },
      onShow: modal => {
        // Optional setup when modal is shown
        // Set up close button event listeners for this specific modal
        this.setupModalCloseButtons(modalId);
        if (options?.onShow) {
          options.onShow(modal);
        }
      },
      onToggle: modal => {
        // Optional toggle handler
        if (options?.onToggle) {
          options.onToggle(modal);
        }
      },
      ...options,
    };

    // Default instance options
    const defaultInstanceOptions: InstanceOptions = {
      id: modalId,
      override: true,
      ...instanceOptions,
    };

    try {
      // Create new modal instance
      const modal: ModalInterface = new Modal(modalElement, defaultOptions, defaultInstanceOptions);

      // Store the instance
      this.modalInstances.set(modalId, modal);

      // Set up close buttons immediately
      this.setupModalCloseButtons(modalId);

      return modal;
    } catch (error) {
      console.error(`[ModalManager] Failed to create modal "${modalId}":`, error);
      return null;
    }
  }

  /**
   * Show modal by ID
   */
  public showModal(
    modalId: string,
    options?: ModalOptions,
    instanceOptions?: InstanceOptions
  ): boolean {
    const modal = this.getModal(modalId, options, instanceOptions);

    if (modal) {
      try {
        modal.show();
        return true;
      } catch (error) {
        console.error(`[ModalManager] Failed to show modal "${modalId}":`, error);
        return false;
      }
    }

    return false;
  }

  /**
   * Hide modal by ID
   */
  public hideModal(modalId: string): boolean {
    const modal = this.modalInstances.get(modalId);

    if (modal) {
      try {
        modal.hide();
        return true;
      } catch (error) {
        console.error(`[ModalManager] Failed to hide modal "${modalId}":`, error);
        return false;
      }
    }

    return false;
  }

  /**
   * Toggle modal by ID
   */
  public toggleModal(
    modalId: string,
    options?: ModalOptions,
    instanceOptions?: InstanceOptions
  ): boolean {
    const modal = this.getModal(modalId, options, instanceOptions);

    if (modal) {
      try {
        modal.toggle();
        return true;
      } catch (error) {
        console.error(`[ModalManager] Failed to toggle modal "${modalId}":`, error);
        return false;
      }
    }

    return false;
  }

  /**
   * Open email subscription modal with tracking data
   */
  public openEmailModal(trackingNumber: string, postalCode: string = ''): boolean {
    // Populate modal with tracking data
    const trackingInput = document.getElementById(
      'gta-subscribe-tracking-number'
    ) as HTMLInputElement;
    const postalInput = document.getElementById('gta-subscribe-postal-code') as HTMLInputElement;
    const emailInput = document.getElementById('gta-subscribe-email') as HTMLInputElement;
    const feedback = document.getElementById('gta-subscribe-feedback') as HTMLElement;

    if (trackingInput) {
      trackingInput.value = trackingNumber;
    }

    if (postalInput) {
      postalInput.value = postalCode;
    }

    // Clear previous values
    if (emailInput) {
      emailInput.value = '';
    }

    if (feedback) {
      feedback.classList.add('hidden');
      feedback.textContent = '';
    }

    // Show the modal
    return this.showModal('gta-email-modal', {
      onShow: () => {
        // Focus on email input when modal opens
        setTimeout(() => {
          if (emailInput) {
            emailInput.focus();
          }
        }, 100);
      },
    });
  }

  /**
   * Open image modal with specified image URL
   */
  public openImageModal(imageUrl: string, altText: string = 'Proof of Delivery Image'): boolean {
    const image = document.getElementById('gta-pod-image') as HTMLImageElement;
    const loadingElement = document.getElementById('gta-image-loading') as HTMLElement;

    if (!image) {
      console.warn('[ModalManager] Image element not found in image modal');
      return false;
    }

    // Show loading state
    if (loadingElement) {
      loadingElement.classList.remove('hidden');
    }

    // Set up image loading
    const tempImage = new Image();
    tempImage.onload = () => {
      // Image loaded successfully
      image.src = imageUrl;
      image.alt = altText;

      if (loadingElement) {
        loadingElement.classList.add('hidden');
      }
    };

    tempImage.onerror = () => {
      // Image failed to load
      const fallbackSrc =
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y5ZmFmYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2Yjc0ODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
      image.src = fallbackSrc;
      image.alt = 'Image not found';

      if (loadingElement) {
        loadingElement.classList.add('hidden');
      }
    };

    // Start loading the image
    tempImage.src = imageUrl;

    // Show the modal
    return this.showModal('gta-image-modal', {
      onHide: () => {
        // Clear image source when modal closes to prevent memory leaks
        setTimeout(() => {
          image.src = '';
          image.alt = '';
        }, 300);
      },
    });
  }

  /**
   * Destroy modal instance and clean up
   */
  public destroyModal(modalId: string): boolean {
    const modal = this.modalInstances.get(modalId);

    if (modal) {
      try {
        // Hide modal first if it's open
        modal.hide();

        // Remove from our tracking
        this.modalInstances.delete(modalId);

        return true;
      } catch (error) {
        console.error(`[ModalManager] Failed to destroy modal "${modalId}":`, error);
        return false;
      }
    }

    return false;
  }

  /**
   * Reinitialize modals for dynamic content
   * Call this after new content is added to the DOM that contains modal triggers
   */
  public reinitializeModals(): void {
    // Clear existing instances to force recreation
    this.modalInstances.clear();

    // Pre-initialize common modals if they exist in DOM
    const commonModalIds = ['gta-email-modal', 'gta-image-modal'];

    commonModalIds.forEach(modalId => {
      const element = document.getElementById(modalId);
      if (element) {
        this.getModal(modalId);
      }
    });
  }

  /**
   * Set up global event delegation for modal triggers
   * This ensures dynamically added buttons work properly
   * Only sets up once to avoid duplicate listeners
   */
  public setupEventDelegation(container: HTMLElement = document.body): void {
    // Only set up once to avoid duplicate event listeners
    if (this.isEventDelegationSetup) {
      return;
    }
    this.isEventDelegationSetup = true;

    // Handle modal close/hide buttons (data-modal-hide)
    container.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;
      const hideButton = target.closest('[data-modal-hide]') as HTMLElement;

      if (hideButton) {
        event.preventDefault();
        const modalId = hideButton.getAttribute('data-modal-hide');
        if (modalId) {
          this.hideModal(modalId);
        }
      }
    });

    // Handle backdrop clicks to close modals
    container.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;

      // Check if clicked element is a modal backdrop (the main modal container)
      if (target.id && target.id.endsWith('-modal') && this.modalInstances.has(target.id)) {
        // Only close if clicking directly on the modal backdrop, not its content
        const modalContent = target.querySelector('.relative');
        if (modalContent && !modalContent.contains(event.target as Node)) {
          this.hideModal(target.id);
        }
      }
    });

    // Handle ESC key to close modals
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Find and close the topmost modal
        const visibleModal = this.getTopmostVisibleModal();
        if (visibleModal) {
          this.hideModal(visibleModal);
        }
      }
    });

    // Handle email modal buttons
    container.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;
      const emailButton = target.closest('.btn-email-modal') as HTMLButtonElement;

      if (emailButton) {
        event.preventDefault();

        const trackingNumber = emailButton.getAttribute('data-tracking') || '';
        const postalCode = emailButton.getAttribute('data-postal') || '';

        this.openEmailModal(trackingNumber, postalCode);
      }
    });

    // Handle image modal buttons
    container.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;
      const imageButton = target.closest('[data-modal-target="gta-image-modal"]') as HTMLElement;

      if (imageButton) {
        event.preventDefault();

        const imageUrl = imageButton.getAttribute('data-image-url');
        const altText = imageButton.getAttribute('data-alt-text') || 'Proof of Delivery Image';

        if (imageUrl) {
          this.openImageModal(imageUrl, altText);
        }
      }
    });

    // Handle Swiper image clicks (for image gallery modals)
    container.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;
      const swiperImg = target.closest('img[data-modal-target]') as HTMLImageElement;

      if (swiperImg && swiperImg.getAttribute('data-modal-target') === 'gta-image-modal') {
        event.preventDefault();

        const imageUrl = swiperImg.getAttribute('data-image-url');
        const altText = swiperImg.alt || 'Proof of Delivery Image';

        if (imageUrl) {
          this.openImageModal(imageUrl, altText);
        }
      }
    });
  }

  /**
   * Get the topmost visible modal ID
   */
  private getTopmostVisibleModal(): string | null {
    for (const [modalId] of this.modalInstances) {
      const modalElement = document.getElementById(modalId);
      if (modalElement && !modalElement.classList.contains('hidden')) {
        return modalId;
      }
    }
    return null;
  }

  /**
   * Set up close buttons for a specific modal
   */
  private setupModalCloseButtons(modalId: string): void {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) {
      return;
    }

    // Find all close buttons within this modal
    const closeButtons = modalElement.querySelectorAll(`[data-modal-hide="${modalId}"]`);

    closeButtons.forEach(button => {
      // Remove any existing listeners to prevent duplicates
      const buttonWithHandler = button as Element & { _gtaModalCloseHandler?: EventListener };
      const existingHandler = buttonWithHandler._gtaModalCloseHandler;
      if (existingHandler) {
        button.removeEventListener('click', existingHandler);
      }

      // Create new handler
      const handler = (event: Event) => {
        event.preventDefault();
        this.hideModal(modalId);
      };

      // Store handler reference and add listener
      buttonWithHandler._gtaModalCloseHandler = handler;
      button.addEventListener('click', handler);
    });
  }

  /**
   * Clean up all modal instances
   */
  public destroyAll(): void {
    const modalIds = Array.from(this.modalInstances.keys());
    modalIds.forEach(modalId => {
      this.destroyModal(modalId);
    });
  }
}

// Export singleton instance for convenience
export const modalManager = ModalManager.getInstance();
